import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { axios, API, logError } from "../lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Backward-compat: some in-flight axios calls still pass Authorization: Bearer ${token}.
  // The real auth is via httpOnly cookie; this is just a transitional shim.
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        setUser(res.data);
      } catch (err) {
        // 401 on first paint = unauthenticated user (expected). Anything else worth logging.
        if (err?.response?.status !== 401) logError("auth: /auth/me failed", err);
        setUser(null);
      }
      finally { setLoading(false); }
    };
    fetchUser();
  }, []);

  const login = useCallback((authToken, userData) => { setToken(authToken); setUser(userData); }, []);
  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); }
    catch (err) { logError("auth: /auth/logout failed", err); }
    setToken(null);
    setUser(null);
  }, []);
  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) { logError("auth: refresh failed", err); }
  }, []);

  // 6 dependencies is intentional — these are the canonical context shape
  // for AuthProvider. login/logout/refresh are wrapped in useCallback so the
  // memo only recomputes on real state changes (user/token/loading).
  const value = useMemo(
    () => ({ user, token, login, logout, refresh, loading }),
    [user, token, login, logout, refresh, loading]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
